import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Typography, Paper, Container } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';

export interface ErrorBoundaryProps {
  /**
   * Child components to render
   */
  children: ReactNode;

  /**
   * Optional fallback component to render instead of default error UI
   */
  fallback?: (
    error: Error,
    errorInfo: ErrorInfo,
    reset: () => void
  ) => ReactNode;

  /**
   * Optional callback when an error is caught
   */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;

  /**
   * Name of the boundary for logging purposes
   */
  boundaryName?: string;

  /**
   * Whether to show technical error details to user (useful for dev/staging)
   */
  showDetails?: boolean;

  /**
   * Custom message to show to users
   */
  errorMessage?: string;

  /**
   * Whether to show the "Go Home" button
   */
  showHomeButton?: boolean;

  /**
   * Custom home URL (defaults to '/')
   */
  homeUrl?: string;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorCount: number;
}

/**
 * Error Boundary component with Application Insights integration
 *
 * Features:
 * - Catches React component errors and prevents app crashes
 * - Logs errors to console with full context
 * - Provides user-friendly error UI with recovery options
 * - Supports custom fallback UI
 * - Tracks error counts to prevent infinite error loops
 * - Integrates with Application Insights for production monitoring
 *
 * @example
 * // Basic usage
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 *
 * @example
 * // With custom error handling
 * <ErrorBoundary
 *   boundaryName="TaskDetails"
 *   onError={(error, errorInfo) => {
 *     trackError(error, { component: 'TaskDetails' });
 *   }}
 *   showDetails={isDevelopment}
 * >
 *   <TaskDetailsPage />
 * </ErrorBoundary>
 *
 * @example
 * // With custom fallback UI
 * <ErrorBoundary
 *   fallback={(error, errorInfo, reset) => (
 *     <CustomErrorScreen error={error} onRetry={reset} />
 *   )}
 * >
 *   <YourComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  private maxErrorCount = 3; // Prevent infinite error loops

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError, boundaryName } = this.props;
    const { errorCount } = this.state;

    // Update error count
    this.setState({
      errorInfo,
      errorCount: errorCount + 1,
    });

    // Log error with full context
    const logContext = {
      boundaryName: boundaryName || 'UnnamedBoundary',
      errorMessage: error.message,
      errorStack: error.stack,
      componentStack: errorInfo.componentStack,
      errorCount: errorCount + 1,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary: ${boundaryName || 'Unknown'}]`, logContext);

    // Call custom error handler if provided
    if (onError) {
      try {
        onError(error, errorInfo);
      } catch (handlerError) {
        // eslint-disable-next-line no-console
        console.error('Error in onError handler:', handlerError);
      }
    }

    // If we're in too many error loops, log a warning
    if (errorCount >= this.maxErrorCount) {
      // eslint-disable-next-line no-console
      console.error(
        `[ErrorBoundary: ${boundaryName}] Error loop detected! Component has crashed ${errorCount} times.`
      );
    }
  }

  resetErrorBoundary = () => {
    const { errorCount } = this.state;

    if (errorCount >= this.maxErrorCount) {
      // eslint-disable-next-line no-console
      console.warn(
        'Error boundary has been reset too many times. Redirecting to home page.'
      );
      window.location.href = this.props.homeUrl || '/';
      return;
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = this.props.homeUrl || '/';
  };

  override render() {
    const { hasError, error, errorInfo, errorCount } = this.state;
    const {
      children,
      fallback,
      showDetails = false,
      errorMessage,
      showHomeButton = true,
      boundaryName,
    } = this.props;

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback) {
        try {
          return fallback(error, errorInfo!, this.resetErrorBoundary);
        } catch (fallbackError) {
          // eslint-disable-next-line no-console
          console.error('Error rendering custom fallback:', fallbackError);
          // Fall through to default UI
        }
      }

      // Check for error loop
      if (errorCount >= this.maxErrorCount) {
        return (
          <Container maxWidth="md">
            <Box
              sx={{
                minHeight: '400px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 4,
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  padding: 4,
                  textAlign: 'center',
                  maxWidth: '600px',
                  width: '100%',
                }}
              >
                <ErrorOutlineIcon
                  color="error"
                  sx={{ fontSize: 64, marginBottom: 2 }}
                />
                <Typography variant="h4" gutterBottom color="error">
                  Critical Error
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  This component has encountered multiple errors. To prevent
                  further issues, please return to the home page.
                </Typography>
                <Box sx={{ marginTop: 3 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<HomeIcon />}
                    onClick={this.handleGoHome}
                    size="large"
                  >
                    Go to Home Page
                  </Button>
                </Box>
              </Paper>
            </Box>
          </Container>
        );
      }

      // Default error UI
      return (
        <Container maxWidth="md">
          <Box
            sx={{
              minHeight: '400px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 4,
            }}
          >
            <Paper
              elevation={3}
              sx={{
                padding: 4,
                textAlign: 'center',
                maxWidth: '600px',
                width: '100%',
              }}
            >
              <ErrorOutlineIcon
                color="error"
                sx={{ fontSize: 64, marginBottom: 2 }}
              />

              <Typography variant="h4" gutterBottom color="error">
                {boundaryName
                  ? `${boundaryName} Error`
                  : 'Something went wrong'}
              </Typography>

              <Typography variant="body1" color="text.secondary" paragraph>
                {errorMessage ||
                  'We encountered an unexpected error. You can try refreshing the page or returning to the home page.'}
              </Typography>

              {showDetails && (
                <Paper
                  variant="outlined"
                  sx={{
                    padding: 2,
                    marginTop: 3,
                    marginBottom: 3,
                    textAlign: 'left',
                    backgroundColor: '#f5f5f5',
                    maxHeight: '200px',
                    overflow: 'auto',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="error"
                    sx={{ fontWeight: 'bold', marginBottom: 1 }}
                  >
                    Error Details:
                  </Typography>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {error.message}
                    {'\n\n'}
                    {error.stack}
                  </Typography>
                  {errorInfo?.componentStack && (
                    <>
                      <Typography
                        variant="subtitle2"
                        color="error"
                        sx={{
                          fontWeight: 'bold',
                          marginTop: 2,
                          marginBottom: 1,
                        }}
                      >
                        Component Stack:
                      </Typography>
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {errorInfo.componentStack}
                      </Typography>
                    </>
                  )}
                </Paper>
              )}

              <Box
                sx={{
                  marginTop: 3,
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={this.resetErrorBoundary}
                >
                  Try Again
                </Button>

                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleReload}
                >
                  Reload Page
                </Button>

                {showHomeButton && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<HomeIcon />}
                    onClick={this.handleGoHome}
                  >
                    Go Home
                  </Button>
                )}
              </Box>

              {errorCount > 1 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ marginTop: 2, display: 'block' }}
                >
                  Error count: {errorCount} / {this.maxErrorCount}
                </Typography>
              )}
            </Paper>
          </Box>
        </Container>
      );
    }

    return children;
  }
}

/**
 * Hook-based wrapper for functional components that need to reset error boundary
 */
export const useErrorHandler = () => {
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return setError;
};

export default ErrorBoundary;
